import React from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import PageLayout from '../../components/layout/PageLayout';
import PageHeader from '../../components/layout/PageHeader';
import { Loading, PanelSkeleton } from '../../components/layout/Skeletons';
import { useAuth } from '../../context/AuthContext';
import { useMyAccount } from '../../hooks/account/useMyAccount';

/**
 * A resident's own account: their handle, their email address, where they
 * live, their password.
 *
 * One form, not three. Changing a handle and moving neighbourhood are the same
 * kind of edit and are saved together; the password sits apart because it is
 * the only field that is never pre-filled and is left alone unless somebody
 * types in it.
 */
function MyAccount() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { form, cities, neighborhoods, loading, saving, error, notice, fieldErrors, handleChange, save } =
    useMyAccount();

  if (authLoading || loading) {
    return (
      <PageLayout centered={false}>
        <Loading label="Chargement">
          <Box sx={{ width: '100%' }}>
            <PanelSkeleton height={420} />
          </Box>
        </Loading>
      </PageLayout>
    );
  }

  // Nothing to edit without an account, and nothing to say about it either.
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const changingPassword = Boolean(form.newPassword);

  return (
    <PageLayout centered={false}>
      <Box sx={{ width: '100%', maxWidth: 680 }}>
        <PageHeader
          title="Mon compte"
          lede="Le nom que vos voisins voient, votre adresse email, votre quartier et votre mot de passe."
        />

        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 } }} component="form" onSubmit={save} noValidate>
          {error && (
            <Alert severity="error" sx={{ mb: 2.5 }}>
              {error}
            </Alert>
          )}

          {notice && (
            <Alert severity="success" sx={{ mb: 2.5 }}>
              {notice}
            </Alert>
          )}

          <Stack spacing={2.5}>
            <TextField
              label="Nom d'utilisateur"
              name="username"
              value={form.username}
              onChange={handleChange}
              error={Boolean(fieldErrors.username)}
              helperText={fieldErrors.username || 'Le nom que vos voisins verront'}
              disabled={saving}
              required
              fullWidth
            />

            <TextField
              label="Adresse email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              error={Boolean(fieldErrors.email)}
              helperText={
                fieldErrors.email || 'Sert à vous connecter. Jamais affichée aux autres habitants.'
              }
              disabled={saving}
              required
              fullWidth
            />

            <Divider textAlign="left" sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Où vous habitez
              </Typography>
            </Divider>

            <TextField
              select
              label="Ville"
              name="citySlug"
              value={form.citySlug}
              onChange={handleChange}
              error={Boolean(fieldErrors.citySlug)}
              helperText={fieldErrors.citySlug}
              disabled={saving}
              fullWidth
            >
              <MenuItem value="">Je préfère ne pas le dire</MenuItem>
              {cities.map((city) => (
                <MenuItem key={city.slug} value={city.slug}>
                  {city.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Quartier"
              name="neighborhoodUuid"
              value={form.neighborhoodUuid}
              onChange={handleChange}
              error={Boolean(fieldErrors.neighborhoodUuid)}
              helperText={
                fieldErrors.neighborhoodUuid ||
                (form.citySlug
                  ? 'Sert à vous montrer ce qui se passe près de chez vous'
                  : 'Choisissez d’abord une ville')
              }
              disabled={saving || !form.citySlug}
              fullWidth
            >
              <MenuItem value="">Toute la ville</MenuItem>
              {neighborhoods.map((neighborhood) => (
                <MenuItem key={neighborhood.uuid} value={neighborhood.uuid}>
                  {neighborhood.name}
                </MenuItem>
              ))}
            </TextField>

            <Divider textAlign="left" sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Mot de passe
              </Typography>
            </Divider>

            <TextField
              label="Nouveau mot de passe"
              name="newPassword"
              type="password"
              value={form.newPassword}
              onChange={handleChange}
              error={Boolean(fieldErrors.newPassword)}
              helperText={fieldErrors.newPassword || 'Laissez vide pour garder le vôtre'}
              disabled={saving}
              autoComplete="new-password"
              fullWidth
            />

            {/* Asked for only when it is needed, so the commonest save — a new
                handle, a new quartier — asks for nothing. */}
            <TextField
              label="Mot de passe actuel"
              name="currentPassword"
              type="password"
              value={form.currentPassword}
              onChange={handleChange}
              error={Boolean(fieldErrors.currentPassword)}
              helperText={
                fieldErrors.currentPassword ||
                'Demandé pour changer votre adresse email ou votre mot de passe'
              }
              disabled={saving}
              autoComplete="current-password"
              required={changingPassword}
              fullWidth
            />
          </Stack>

          <Button
            type="submit"
            variant="contained"
            size="large"
            startIcon={<SaveIcon />}
            disabled={saving}
            sx={{ mt: 3 }}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </Paper>
      </Box>
    </PageLayout>
  );
}

export default MyAccount;
